import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  ChartedSurface,
  isSameToothRef,
  stafferToNamedDoc,
  toChartedRef,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  ChartableSurface,
  IChartedCondition,
  IChartedItemDetail,
  IChartedSurface,
  IChartedTreatment,
  isChartedCondition,
  isChartedTreatment,
  isKnownChartableSurface,
  ITooth,
  ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  snapshot,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';

export class FixMissingChartedItemSurfaces {
  constructor(
    private _itemDetail: IChartedItemDetail,
    private _tooth: ITooth,
    private _chartStore: ChartFacade,
    private _chartId: ChartId
  ) {}

  getSurfacesForTooth(): ChartableSurface[] {
    return compact(
      this._itemDetail.item.chartedSurfaces
        .filter(
          (chartedSurface) =>
            chartedSurface.chartedRef.tooth &&
            isSameToothRef(
              chartedSurface.chartedRef.tooth,
              this._tooth.toothRef
            )
        )
        .map((chartedSurface) => chartedSurface.chartedRef.tooth?.surface)
    ).filter((surface) => isKnownChartableSurface(surface));
  }

  async buildChartedRefsFromSelectedSurfaces(
    selectedSurfaces: ChartableSurface[]
  ): Promise<IChartedSurface[]> {
    const chartingAs = await snapshot(
      this._chartStore.chartingAs$(this._chartId)
    );
    const chartedSurface = first(this._itemDetail.item.chartedSurfaces);
    if (!chartedSurface) {
      throw new Error(
        `No charted surfaces found for ${this._itemDetail.config.name}.`
      );
    }

    const tooth = this._tooth;

    return selectedSurfaces.map((surface) =>
      ChartedSurface.init({
        chartedRef: toChartedRef({
          tooth,
          surface,
        }),
        chartedBy: stafferToNamedDoc(chartingAs),
        resolvedBy: chartedSurface.resolvedBy,
        resolvedAt: chartedSurface.resolvedAt ?? chartedSurface.chartedAt,
      })
    );
  }

  async updateChartedItemSurfaces(
    updatedChartedRefs: IChartedSurface[]
  ): Promise<void> {
    if (isChartedTreatment(this._itemDetail.item) && this._itemDetail.stepRef) {
      await this._updateChartedTreatmentSurfaces(
        this._itemDetail.item,
        this._itemDetail.stepRef,
        updatedChartedRefs
      );
      return;
    }

    if (isChartedCondition(this._itemDetail.item)) {
      const condition = this._itemDetail.item;
      await this._updateChartedConditionSurfaces(condition, updatedChartedRefs);
    }
  }

  private async _updateChartedConditionSurfaces(
    condition: IChartedCondition,
    updatedChartedRefs: IChartedSurface[]
  ): Promise<void> {
    const conditions = await snapshot(
      this._chartStore.chartedConditions$(this._chartId)
    );
    const currentCondition = conditions.find(
      (chartCondition) => chartCondition.uuid === condition.uuid
    );

    if (!currentCondition) {
      throw new Error(
        `Condition with ID ${condition.uuid} not found in charted conditions.`
      );
    }

    currentCondition.chartedSurfaces = [
      ...updatedChartedRefs,
      ...currentCondition.chartedSurfaces.filter((chartedSurface) => {
        if (!chartedSurface.chartedRef.tooth) {
          return true;
        }

        if (
          isSameToothRef(chartedSurface.chartedRef.tooth, this._tooth.toothRef)
        ) {
          return false;
        }

        return true;
      }),
    ];

    this._chartStore.updateCondition(this._chartId, currentCondition);
  }

  private async _updateChartedTreatmentSurfaces(
    treatment: IChartedTreatment,
    stepRef: DocumentReference<ITreatmentStep>,
    updatedChartedRefs: IChartedSurface[]
  ): Promise<void> {
    treatment.chartedSurfaces = updatedChartedRefs;

    const step = await Firestore.getDoc(stepRef);
    const treatments = TreatmentStep.updateTreatment(step, treatment);

    await Firestore.patchDoc(stepRef, treatments);
  }
}
